import React, { Reducer, FC, useReducer, ChangeEvent, useEffect } from 'react'
import { FieldErrors } from 'react-hook-form'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Box, Divider, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { Radio, IRadioProps } from 'transfix-ui'
import { IntegrationMessage } from './IntegrationMessage'
import { TRateConfirmationFormSchema } from './components/RateConfirmationForm/schema/RateConfirmationFormSchema'
import { TInputRefType } from './components/RateConfirmationForm/types'

interface IRadioWithDescriptionProps extends IRadioProps {
  label: string
  description: string
}
type TTrackingProviderValue = TRACKING_METHOD_TYPE | ''
type TTrackingMethodValues = 'eld' | TRACKING_METHOD_TYPE.fmsMobile | ''
interface IIlineMessageByProvider {
  provider: TRACKING_METHOD_TYPE
}

const RadioWithDescription = ({
  description,
  label,
  ...radioProps
}: IRadioWithDescriptionProps) => (
  <Box>
    <Radio
      {...radioProps}
      composed
      label={
        <Box>
          <Box color='textPrimary' fontWeight={700}>
            {label}
          </Box>
          <Box color='textSecondary'>{description}</Box>
        </Box>
      }
    />
  </Box>
)

const InlineMessageByProvider: FC<IIlineMessageByProvider> = ({ provider }) => {
  const eld = provider === TRACKING_METHOD_TYPE.keepTruckin ? 'KeepTruckin' : 'project44'

  return (
    <Box mt={2}>
      <InlineMessage
        message={
          <>
            <Box fontWeight='bold'>You are set up to track with {eld}</Box>
            <Box>
              Your carrier is connected to a {eld} account.{' '}
              <Link to='/user/settings/integrations' target='_blank'>
                ELD Settings
              </Link>
            </Box>
          </>
        }
      />
    </Box>
  )
}

interface ITrackInfo {
  provider: TTrackingProviderValue
  method: TTrackingMethodValues
}
type TxByActionType = {
  [key in keyof ITrackInfo]: (
    state: ITrackInfo,
    value: string,
    defaultValue?: TRACKING_METHOD_TYPE
  ) => ITrackInfo
}

interface ITrackingMethodProps {
  isKeepTruckinConnected: boolean
  isProject44Connected: boolean
  carrierId?: number
  formErrors: FieldErrors<TRateConfirmationFormSchema>
  defaultValue?: TRACKING_METHOD_TYPE
  onChange: (t: TRACKING_METHOD_TYPE) => void
  inputRef?: TInputRefType['trackingMethod']
}

function trackingMethodReducer(state: ITrackInfo, values: Partial<ITrackInfo>) {
  return {
    ...state,
    ...values,
  }
}

const txByAction: TxByActionType = {
  method: (state: ITrackInfo, value: string, defaultValue?: TRACKING_METHOD_TYPE) => {
    if (defaultValue === TRACKING_METHOD_TYPE.keepTruckin && value === 'eld') {
      return {
        provider: TRACKING_METHOD_TYPE.keepTruckin,
        method: 'eld',
      }
    }
    return {
      provider: value !== state.method ? '' : state.provider,
      method: value as TTrackingMethodValues,
    }
  },
  provider: (state: ITrackInfo, value: string) => ({
    ...state,
    provider: value as TTrackingProviderValue,
  }),
}

const getReducerValue = (defaultValue: TRACKING_METHOD_TYPE | undefined): ITrackInfo => {
  if (!defaultValue) {
    return {
      provider: '',
      method: '',
    }
  }
  const method = defaultValue === TRACKING_METHOD_TYPE.fmsMobile ? defaultValue : 'eld'
  const provider = defaultValue !== TRACKING_METHOD_TYPE.fmsMobile ? defaultValue : ''

  return {
    method,
    provider,
  }
}

const useStyles = makeStyles(theme => ({
  errorLabel: {
    color: brandColors.error,
  },
  divider: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  trackingRequiredMessage: {
    color: brandColors.error,
    marginBottom: theme.spacing(3),
  },
}))

export const TrackingMethod: FC<ITrackingMethodProps> = ({
  isKeepTruckinConnected,
  isProject44Connected,
  carrierId,
  formErrors,
  defaultValue,
  onChange = () => {},
  inputRef,
}) => {
  const classes = useStyles()

  const [{ method: trackingMethod, provider: trackingProvider }, dispatch] = useReducer<
    Reducer<ITrackInfo, Partial<ITrackInfo>>
  >(trackingMethodReducer, getReducerValue(defaultValue))

  const onTrackingChange = (action: keyof ITrackInfo) => ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    let newState: ITrackInfo = {
      provider: trackingProvider,
      method: trackingMethod,
    }
    const txAction = txByAction[action]

    if (txAction) {
      newState = txAction(newState, value, defaultValue)
    }

    dispatch(newState)

    if (newState.method === TRACKING_METHOD_TYPE.fmsMobile) {
      onChange(newState.method)
    } else if (newState.provider) {
      onChange(newState.provider)
    }
  }

  // Pre-select radio buttons from defaultValue prop.
  useEffect(() => {
    const newValues = getReducerValue(defaultValue)
    dispatch(newValues)
  }, [defaultValue])

  const { trackingMethod: { message = '' } = {} } = formErrors
  const legendClass = classNames('label', 'required', message ? classes.errorLabel : '')

  return (
    <div ref={inputRef}>
      <Box mb={1}>
        <Box mb={3} p={3} bgcolor='#F3F5F7'>
          <Typography className={classes.trackingRequiredMessage} variant='subtitle2'>
            Location Tracking Required
          </Typography>
          <Box component='legend' className={legendClass}>
            Select your tracking method
          </Box>
          <RadioWithDescription
            id='tracking-method'
            label="My Truck's ELD"
            description='Share your location using your truck’s Electronic Logging Device.'
            checked={trackingMethod === 'eld'}
            onChange={onTrackingChange('method')}
            dataTest='eld-tracking-method'
            value='eld'
          />
          <RadioWithDescription
            id='tracking-method'
            label='Transfix mobile app'
            description='Your driver must have the Transfix app installed, be signed in, and have location sharing set to "always."'
            checked={trackingMethod === TRACKING_METHOD_TYPE.fmsMobile}
            onChange={onTrackingChange('method')}
            value={TRACKING_METHOD_TYPE.fmsMobile}
            dataTest='transfix-tracking-method'
          />

          {trackingMethod === 'eld' && defaultValue !== TRACKING_METHOD_TYPE.keepTruckin && (
            <Box mt={2} mb={2}>
              <Box component='legend' className={legendClass}>
                Select ELD provider
              </Box>
              <RadioWithDescription
                id='tracking-provider'
                label='KeepTruckin'
                description="Share your truck's location via your KeepTruckin ELD."
                checked={trackingProvider === TRACKING_METHOD_TYPE.keepTruckin}
                onChange={onTrackingChange('provider')}
                dataTest='keep-truckin-provider'
                value={TRACKING_METHOD_TYPE.keepTruckin}
              />
              <RadioWithDescription
                id='tracking-provider'
                label='I have another ELD provider'
                description='Share tracking updates from one of 750+ ELD providers using a project44 account.'
                checked={trackingProvider === TRACKING_METHOD_TYPE.project44}
                onChange={onTrackingChange('provider')}
                value={TRACKING_METHOD_TYPE.project44}
                dataTest='p44-provider'
              />
            </Box>
          )}

          {trackingProvider === TRACKING_METHOD_TYPE.keepTruckin &&
            (isKeepTruckinConnected ? (
              <InlineMessageByProvider provider={TRACKING_METHOD_TYPE.keepTruckin} />
            ) : (
              <IntegrationMessage
                trackingProvider={TRACKING_METHOD_TYPE.keepTruckin}
                carrierId={carrierId}
              />
            ))}
          {trackingProvider === TRACKING_METHOD_TYPE.project44 && !isProject44Connected && (
            <IntegrationMessage
              trackingProvider={TRACKING_METHOD_TYPE.project44}
              carrierId={carrierId}
            />
          )}
          <Divider className={classes.divider} />
          <Typography color='textPrimary' variant='subtitle1'>
            Tracking Requirement
          </Typography>
          <Box>
            Location services must be enabled for the entire transit beginning 1 hour before arrival
            at pickup through delivery.
          </Box>
          <Box mt={3} mb={2}>
            <Typography color='textPrimary' variant='subtitle1'>
              Detention Requirement
            </Typography>
            <Box>
              The driver must be actively tracking via an approved method, or notify Transfix 30
              minutes prior to entering detention or the carrier will be ineligible for additional
              compensation
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  )
}
